// src/views/unidades/UnidadesList.jsx
import React, { useState, useEffect, useCallback } from "react";
import unidadesService from "../../services/unidadesService";
import condominiosService from "../../services/condominiosService";
import { useAuth } from "../../contexts/AuthContext";
import UnidadeModal from "./UnidadeModal";
import GerarUnidadesModal from "./GerarUnidadesModal";
import DeleteModal from "../../components/ui/DeleteModal";

const PER_PAGE = 10;

export default function UnidadesList() {
  const { condominioAtivo, podeEditar } = useAuth();
  const canEdit = podeEditar("unidades");
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [pagination, setPagination] = useState({ total:0, totalPages:1 });
  const [stats, setStats]         = useState({ total:0, ocupadas:0, vagas:0, veiculos:0, pets:0, sem_email:0, sem_whatsapp:0 });

  const [condominios, setCondominios] = useState([]);
  const [idCondo, setIdCondo]         = useState("");

  // Sync with navbar condominioAtivo
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  const [search, setSearch]           = useState("");
  const [filterBloco, setFilterBloco] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [blocos, setBlocos]           = useState([]);
  const [page, setPage]               = useState(1);

  const [showModal, setShowModal]       = useState(false);
  const [showGerar, setShowGerar]       = useState(false);
  const [editData, setEditData]         = useState(null);
  const [showDelete, setShowDelete]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving]             = useState(false);
  const [modalError, setModalError]     = useState(null);
  const [deleting, setDeleting]         = useState(false);

  useEffect(() => {
    condominiosService.listar({ limit:100 }).then(r => {
      setCondominios(r.data);
    }).catch(()=>{});
  }, []);

  useEffect(() => {
    if (!idCondo) return;
    unidadesService.listarBlocos(idCondo).then(r=>setBlocos(r.data)).catch(()=>{});
    unidadesService.stats(idCondo).then(r=>setStats(r.data||{})).catch(()=>{});
  }, [idCondo]);

  const carregar = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true); setError(null);
    try {
      const res = await unidadesService.listar({ idCondominio:idCondo, search, bloco:filterBloco, status:filterStatus, page, limit:PER_PAGE });
      setData(res.data);
      setPagination(res.pagination);
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  }, [idCondo, search, filterBloco, filterStatus, page]);

  const carregarStats = useCallback(() => {
    if (!idCondo) return;
    unidadesService.stats(idCondo).then(r=>setStats(r.data||{})).catch(()=>{});
  }, [idCondo]);

  useEffect(() => { carregar(); }, [carregar]);

  // Filtro rápido pelos cards de KPI
  const aplicarFiltroCard = (k) => {
    setPage(1);
    if (k === "ocupadas") setFilterStatus("ocupado");
    else if (k === "vagas") setFilterStatus("vago");
    else if (k === "sem_email") setFilterStatus("sem_email");
    else setFilterStatus("");
  };

  const condoNome = condominios.find(c=>String(c.id)===String(idCondo))?.nmFantasia || "";
  const totalPages = pagination.totalPages || 1;

  const handleSave = async (form) => {
    setSaving(true); setModalError(null);
    try {
      const payload = { ...form, idCondominio: idCondo };
      if (form.id) await unidadesService.atualizar(form.id, payload);
      else         await unidadesService.criar(payload);
      setShowModal(false);
      await carregar();
      carregarStats();
    } catch(err) { setModalError(err.message||"Erro ao salvar a unidade."); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await unidadesService.deletar(deleteTarget.id);
      setShowDelete(false); setDeleteTarget(null);
      await carregar();
      carregarStats();
    } catch(err) {
      setError(err.message||"Erro ao excluir a unidade.");
      setShowDelete(false);
    }
    finally { setDeleting(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title"><i className="bi bi-house-door me-2" style={{color:"var(--primary)",fontSize:22}}/>Unidades</h1>
          <p className="page-subtitle">Gerencie as unidades, condôminos, veículos e PETs</p>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">

          <button className="btn btn-outline-secondary" onClick={()=>setShowGerar(true)} disabled={!idCondo}><i className="bi bi-grid-3x3"/> Gerar em Lote</button>
          <button className="btn btn-primary" onClick={()=>{ setEditData(null); setShowModal(true); }} disabled={!idCondo || !canEdit}><i className="bi bi-plus-lg"/> Nova Unidade</button>
        </div>
      </div>

      {/* Stats clicáveis */}
      <div className="row g-3 mb-3">
        {[
          {key:"total",     icon:"bi-house-door",  cls:"blue",   val:stats.total,     label:"Total"},
          {key:"ocupadas",  icon:"bi-house-check", cls:"green",  val:stats.ocupadas,  label:"Ocupadas"},
          {key:"vagas",     icon:"bi-house",       cls:"orange", val:stats.vagas,     label:"Vagas"},
          {key:"sem_email", icon:"bi-envelope-slash", cls:"red", val:stats.sem_email, label:"Sem e-mail"},
        ].map(s=>(
          <div className="col-6 col-md-3" key={s.key}>
            <div className="stat-card" onClick={()=>aplicarFiltroCard(s.key)}
              style={{cursor:idCondo?"pointer":"default", transition:"transform .15s"}}
              onMouseEnter={e=>e.currentTarget.style.transform = idCondo ? "translateY(-2px)" : ""}
              onMouseLeave={e=>e.currentTarget.style.transform = ""}>
              <div className={`stat-icon ${s.cls}`}><i className={`bi ${s.icon}`}/></div>
              <div><div className="stat-value">{s.val ?? 0}</div><div className="stat-label">{s.label}</div></div>
            </div>
          </div>
        ))}
      </div>

      {/* KPIs secundários */}
      <div className="d-flex flex-wrap gap-3 mb-3" style={{fontSize:12, color:"var(--gray-600)"}}>
        <span><i className="bi bi-car-front me-1 text-primary"/>Veículos: <strong>{stats.veiculos||0}</strong></span>
        <span><i className="bi bi-heart me-1" style={{color:"#BE123C"}}/>PETs: <strong>{stats.pets||0}</strong></span>
        <span><i className="bi bi-whatsapp me-1" style={{color:"#25D366"}}/>Sem WhatsApp: <strong>{stats.sem_whatsapp||0}</strong></span>
      </div>

      <div className="card">
        <div className="card-header-custom">
          <div className="search-wrapper">
            <i className="bi bi-search"/>
            <input className="search-input" placeholder="Buscar unidade, bloco, condômino..." value={search} onChange={e=>{ setSearch(e.target.value); setPage(1); }}/>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <select className="form-select form-select-sm" style={{width:"auto",fontSize:13}} value={filterBloco} onChange={e=>{ setFilterBloco(e.target.value); setPage(1); }}>
              <option value="">Todos os blocos</option>
              {blocos.map(b=><option key={b} value={b}>Bloco {b}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{width:"auto",fontSize:13}} value={filterStatus} onChange={e=>{ setFilterStatus(e.target.value); setPage(1); }}>
              <option value="">Todos</option>
              <option value="ocupado">Ocupadas</option>
              <option value="vago">Vagas</option>
              <option value="sem_email">Sem e-mail</option>
            </select>
            <button className="btn btn-outline-secondary btn-sm" onClick={carregar}><i className="bi bi-arrow-clockwise"/></button>
          </div>
        </div>

        {error && <div className="alert alert-danger m-3 mb-0" style={{fontSize:13}}><i className="bi bi-exclamation-triangle-fill me-2"/>{error}</div>}

        <div className="table-responsive">
          <table className="table table-custom">
            <thead>
              <tr><th>Unidade</th><th>Bloco</th><th>Ocupante(s)</th><th>Contato envio</th><th>Área m²</th><th>Venc.</th><th>Veíc.</th><th>PETs</th><th>Situação</th><th style={{width:100}}>Ações</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-5"><span className="spinner-border spinner-border-sm text-primary me-2"/><span style={{color:"var(--gray-500)"}}>Carregando...</span></td></tr>
              ) : !idCondo ? (
                <tr><td colSpan={10} className="text-center py-5" style={{color:"var(--gray-400)"}}><i className="bi bi-buildings" style={{fontSize:32,display:"block",marginBottom:8}}/>Selecione um condomínio</td></tr>
              ) : data.length===0 ? (
                <tr><td colSpan={10} className="text-center py-5" style={{color:"var(--gray-400)"}}><i className="bi bi-house" style={{fontSize:32,display:"block",marginBottom:8}}/>Nenhuma unidade encontrada</td></tr>
              ) : data.map(u=>(
                <tr key={u.id}>
                  <td style={{fontWeight:700,color:"var(--primary)",fontSize:14}}>{u.unidade}</td>
                  <td>{u.bloco||<span style={{color:"var(--gray-300)"}}>—</span>}</td>
                  <td>
                    {u.nmCondomino || u.nmInquilino
                      ? <div>
                          {u.nmCondomino && (
                            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:u.nmInquilino?3:0}}>
                              <span style={{background:"var(--bg-highlight)",color:"var(--primary)",padding:"1px 7px",borderRadius:10,fontSize:10.5,fontWeight:700,flexShrink:0}}>PROP</span>
                              <span style={{fontWeight:600,fontSize:12.5}}>{u.nmCondomino}</span>
                            </div>
                          )}
                          {u.nmInquilino && (
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <span style={{background:"#FFF7ED",color:"#C2410C",padding:"1px 7px",borderRadius:10,fontSize:10.5,fontWeight:700,flexShrink:0}}>INQ</span>
                              <span style={{fontSize:12.5,color:"var(--gray-600)"}}>{u.nmInquilino}</span>
                            </div>
                          )}
                        </div>
                      : <span style={{color:"var(--gray-400)",fontStyle:"italic",fontSize:13}}>— Vago —</span>
                    }
                  </td>
                  <td style={{fontSize:12}}>
                    {u.emailDestino
                      ? <div title="E-mail efetivo (vínculo > condômino > unidade)" style={{color:"var(--gray-700)",overflow:"hidden",textOverflow:"ellipsis",maxWidth:180,whiteSpace:"nowrap"}}>
                          <i className="bi bi-envelope-check me-1 text-success"/>{u.emailDestino}
                        </div>
                      : <div style={{color:"#EF4444",fontSize:11.5}}>
                          <i className="bi bi-envelope-slash me-1"/>Sem e-mail
                        </div>
                    }
                    {u.whatsappDestino && <div style={{color:"var(--gray-500)",fontSize:11}}>
                      <i className="bi bi-whatsapp me-1" style={{color:"#25D366"}}/>{u.whatsappDestino}
                    </div>}
                  </td>
                  <td style={{fontSize:13}}>{u.areM2?`${u.areM2} m²`:"—"}</td>
                  <td>
                    {u.diaVencimentoDif
                      ? <span style={{background:"var(--gray-100)",padding:"2px 8px",borderRadius:4,fontWeight:600,color:"var(--primary)",fontSize:12}}>Dia {String(u.diaVencimentoDif).padStart(2,"0")}</span>
                      : <span style={{color:"var(--gray-300)"}}>—</span>
                    }
                  </td>
                  <td>
                    <span style={{background:u.qtdVeiculos>0?"#EFF6FF":"var(--gray-100)",color:u.qtdVeiculos>0?"var(--primary)":"var(--gray-400)",padding:"2px 10px",borderRadius:12,fontSize:12,fontWeight:600}}>
                      <i className="bi bi-car-front me-1"/>{u.qtdVeiculos||0}
                    </span>
                  </td>
                  <td>
                    <span style={{background:u.qtdPets>0?"#FFF0F3":"var(--gray-100)",color:u.qtdPets>0?"#BE123C":"var(--gray-400)",padding:"2px 10px",borderRadius:12,fontSize:12,fontWeight:600}}>
                      <i className="bi bi-heart me-1"/>{u.qtdPets||0}
                    </span>
                  </td>
                  <td>
                    {(u.nmCondomino || u.nmInquilino || u.qtdVinculosAtivos > 0)
                      ? <span className="badge-status badge-ativo"><i className="bi bi-circle-fill" style={{fontSize:7}}/>Ocupada</span>
                      : <span style={{background:"var(--bg-warning-soft)",color:"#854D0E",padding:"3px 10px",borderRadius:20,fontSize:11.5,fontWeight:600}}><i className="bi bi-circle-fill me-1" style={{fontSize:7}}/>Vago</span>
                    }
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-outline-secondary btn-sm btn-icon" onClick={()=>{ setEditData(u); setShowModal(true); }} disabled={!canEdit}><i className="bi bi-pencil"/></button>
                      <button className="btn btn-outline-danger btn-sm btn-icon" onClick={()=>{ setDeleteTarget(u); setShowDelete(true); }} disabled={!canEdit}><i className="bi bi-trash3"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination-wrapper">
          <span className="pagination-info">{pagination.total===0?"Nenhum registro":`Exibindo ${(page-1)*PER_PAGE+1}–${Math.min(page*PER_PAGE,pagination.total)} de ${pagination.total} registros`}</span>
          <nav><ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${page===1?"disabled":""}`}><button className="page-link" onClick={()=>setPage(p=>p-1)}><i className="bi bi-chevron-left"/></button></li>
            {Array.from({length:totalPages},(_,i)=>(<li key={i+1} className={`page-item ${page===i+1?"active":""}`}><button className="page-link" onClick={()=>setPage(i+1)}>{i+1}</button></li>))}
            <li className={`page-item ${page===totalPages?"disabled":""}`}><button className="page-link" onClick={()=>setPage(p=>p+1)}><i className="bi bi-chevron-right"/></button></li>
          </ul></nav>
        </div>
      </div>

      <UnidadeModal show={showModal} onClose={()=>{setShowModal(false);setModalError(null);}} onSave={handleSave}
        editData={editData} saving={saving} idCondominio={idCondo} condominioNome={condoNome}
        serverError={modalError}/>
      <GerarUnidadesModal show={showGerar} onClose={()=>setShowGerar(false)} onGerado={carregar}
        idCondominio={idCondo} condominioNome={condoNome}/>
      <DeleteModal show={showDelete} onClose={()=>setShowDelete(false)} onConfirm={handleDelete}
        itemName={deleteTarget?`Unidade ${deleteTarget.unidade}${deleteTarget.bloco?" - Bloco "+deleteTarget.bloco:""}`:""}
        loading={deleting}/>
    </div>
  );
}
